/**
 * File Validation Utility for Pentest Module
 * 
 * This utility provides comprehensive file validation to prevent file extension vulnerabilities:
 * - Validates file extensions against allowlist
 * - Detects and blocks double extensions
 * - Validates MIME types
 * - Sanitizes file names
 * - Checks file sizes
 * 
 * Supported formats: PDF, DOC, DOCX, TXT, MD, JSON, XML, CSV
 */

// Maximum file size: 100MB
export const MAX_FILE_SIZE = 100 * 1024 * 1024;

// Allowed file extensions with their corresponding MIME types
export const ALLOWED_FILE_TYPES: { [key: string]: string[] } = {
  'pdf': ['application/pdf'],
  'doc': ['application/msword'],
  'docx': [
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ],
  'txt': ['text/plain'],
  'md': ['text/markdown', 'text/plain'],
  'json': ['application/json', 'text/plain'],
  'xml': ['application/xml', 'text/xml'],
  'csv': ['text/csv', 'text/plain', 'application/vnd.ms-excel']
};

// Malicious file extensions that should always be blocked
const BLOCKED_EXTENSIONS = [
  'exe', 'bat', 'cmd', 'com', 'pif', 'scr', 'vbs', 'js', 'jar',
  'dll', 'sys', 'ps1', 'sh', 'bash', 'msi', 'app', 'deb', 'rpm',
  'html', 'htm', 'hta', 'svg', 'php', 'asp', 'aspx', 'jsp'
];

/**
 * Validation result interface
 */
export interface FileValidationResult {
  valid: boolean;
  error?: string;
  sanitizedFileName?: string;
}

/**
 * Extract all extensions from a filename (for detecting double extensions)
 */
function getAllExtensions(fileName: string): string[] {
  const parts = fileName.split('.');
  if (parts.length <= 1) {
    return [];
  }
  // Get all parts after the first one (which is the base name)
  return parts.slice(1).map(ext => ext.toLowerCase());
}

/**
 * Check for double extensions in filename
 * Examples: file.pdf.exe, document.txt.bat
 */
export function hasDoubleExtension(fileName: string): boolean {
  const extensions = getAllExtensions(fileName);
  
  // If there are multiple extensions, check if any are malicious
  if (extensions.length > 1) {
    return true; // Any double extension is suspicious
  }
  
  return false;
}

/**
 * Check if the file extension is explicitly blocked
 */
function hasBlockedExtension(fileName: string): boolean {
  const extensions = getAllExtensions(fileName);
  
  // Check if any extension is in the blocked list
  return extensions.some(ext => BLOCKED_EXTENSIONS.includes(ext));
}

/**
 * Get the file extension from filename
 */
export function getFileExtension(fileName: string): string | null {
  const match = fileName.match(/\.([^.]+)$/);
  return match ? match[1].toLowerCase() : null;
}

/**
 * Validate file extension against allowlist
 */
export function isValidExtension(extension: string | null): boolean {
  if (!extension) {
    return false;
  }
  return Object.keys(ALLOWED_FILE_TYPES).includes(extension.toLowerCase());
}

/**
 * Validate MIME type against the file extension
 */
export function isValidMimeType(extension: string, mimeType: string): boolean {
  const allowedMimeTypes = ALLOWED_FILE_TYPES[extension.toLowerCase()];
  if (!allowedMimeTypes) {
    return false;
  }
  return allowedMimeTypes.includes(mimeType);
}

/**
 * Sanitize file name by removing potentially dangerous characters
 * Keeps only alphanumeric, dash, underscore, space, and dot
 */
export function sanitizeFileName(fileName: string): string {
  // Remove any path traversal attempts
  fileName = fileName.replace(/\.\.\//g, '').replace(/\.\.\\/g, '');
  
  // Remove or replace dangerous characters
  // Keep: letters, numbers, spaces, dots, dashes, underscores
  fileName = fileName.replace(/[^a-zA-Z0-9._\-\s]/g, '_');
  
  // Remove multiple consecutive dots (could be used for obfuscation)
  fileName = fileName.replace(/\.{2,}/g, '.');
  
  // Trim whitespace
  fileName = fileName.trim();
  
  // Limit length
  const maxLength = 255;
  if (fileName.length > maxLength) {
    const extension = getFileExtension(fileName);
    const baseName = fileName.substring(0, fileName.lastIndexOf('.'));
    const truncatedBase = baseName.substring(0, maxLength - (extension ? extension.length + 1 : 0));
    fileName = extension ? `${truncatedBase}.${extension}` : truncatedBase;
  }
  
  return fileName;
}

/**
 * Validate file size
 */
export function isValidFileSize(fileSize: number, maxSize: number = MAX_FILE_SIZE): boolean {
  return fileSize > 0 && fileSize <= maxSize;
}

/**
 * Comprehensive file validation
 * Performs all security checks on a file before upload
 */
export function validateFile(file: File): FileValidationResult {
  // 1. Check file size
  if (!isValidFileSize(file.size)) {
    return {
      valid: false,
      error: `File size exceeds maximum allowed size of ${MAX_FILE_SIZE / (1024 * 1024)}MB`
    };
  }

  // 2. Check for zero-byte files
  if (file.size === 0) {
    return {
      valid: false,
      error: 'Empty files are not allowed'
    };
  }

  // 3. Sanitize file name
  const sanitizedFileName = sanitizeFileName(file.name);

  // 4. Check for double extensions
  if (hasDoubleExtension(file.name)) {
    return {
      valid: false,
      error: 'Files with double extensions are not allowed. Please rename the file with a single extension.'
    };
  }

  // 5. Check for explicitly blocked extensions
  if (hasBlockedExtension(file.name)) {
    return {
      valid: false,
      error: 'This file type is not allowed for security reasons.'
    };
  }

  // 6. Validate file extension
  const extension = getFileExtension(file.name);
  if (!extension || !isValidExtension(extension)) {
    const allowedExts = Object.keys(ALLOWED_FILE_TYPES).join(', ');
    return {
      valid: false,
      error: `Invalid file extension. Allowed formats: ${allowedExts.toUpperCase()}`
    };
  }

  // 7. Validate MIME type
  if (!isValidMimeType(extension, file.type)) {
    const expectedTypes = ALLOWED_FILE_TYPES[extension].join(', ');
    return {
      valid: false,
      error: `File type mismatch. Expected MIME type for .${extension}: ${expectedTypes}, but got: ${file.type || 'unknown'}`
    };
  }

  // 8. Additional check: ensure MIME type is not empty
  if (!file.type || file.type.trim() === '') {
    return {
      valid: false,
      error: 'File type could not be determined. Please ensure the file is not corrupted.'
    };
  }

  // All checks passed
  return {
    valid: true,
    sanitizedFileName
  };
}

/**
 * Get a user-friendly error message for validation failures
 */
export function getValidationErrorMessage(result: FileValidationResult): string {
  return result.error || 'File validation failed';
}

/**
 * Get the accept attribute value for file input elements
 */
export function getAcceptAttribute(): string {
  return '.pdf,.doc,.docx,.txt,.md,.json,.xml,.csv';
}

/**
 * Get a formatted string of allowed file types for display
 */
export function getAllowedFileTypesDisplay(): string {
  return 'PDF, DOC, DOCX, TXT, MD, JSON, XML, CSV';
}
